import { mount } from 'svelte';
import './styles/global.css';
import App from './App.svelte';
import { getTelegramWebApp } from './lib/telegram';

const LIGHT_BG = '#ffffff';
const DARK_BG = '#16101F';

function applyTheme(scheme: 'light' | 'dark'): void {
  document.documentElement.setAttribute('data-theme', scheme);
  // Match WebView canvas to active theme — kills dark flash on route changes.
  getTelegramWebApp()?.setBackgroundColor?.(scheme === 'dark' ? DARK_BG : LIGHT_BG);
}

/** Dev-only manual override set via DevStatusSwitcher. Returns null when the
 *  user hasn't pinned a theme — in that case we fall back to Telegram /
 *  prefers-color-scheme as before. */
function getForcedTheme(): 'light' | 'dark' | null {
  const v = localStorage.getItem('focus:dev-theme');
  return v === 'dark' || v === 'light' ? v : null;
}

const tg = getTelegramWebApp();
if (tg) {
  tg.ready();
  tg.expand();
  applyTheme(getForcedTheme() ?? (tg.colorScheme === 'dark' ? 'dark' : 'light'));
  // Re-apply on live theme switch inside Telegram (system → other appearance).
  tg.onEvent?.('themeChanged', () => {
    applyTheme(getForcedTheme() ?? (tg.colorScheme === 'dark' ? 'dark' : 'light'));
  });
} else {
  // Outside Telegram (browser): honour OS preference via media query, unless
  // the dev override is set.
  const mq = window.matchMedia('(prefers-color-scheme: dark)');
  applyTheme(getForcedTheme() ?? (mq.matches ? 'dark' : 'light'));
  mq.addEventListener('change', (e) => {
    applyTheme(getForcedTheme() ?? (e.matches ? 'dark' : 'light'));
  });
}

// iOS Safari (since iOS 10) ignores `user-scalable=no` in the viewport meta,
// so we still need JS guards for pinch-zoom and the double-tap-to-zoom
// gesture. `touch-action: manipulation` on html/body (set in global.css)
// covers Android; these listeners cover iOS.
window.addEventListener(
  'gesturestart',
  (e) => e.preventDefault(),
  { passive: false },
);
window.addEventListener(
  'dblclick',
  (e) => e.preventDefault(),
  { passive: false },
);

const app = mount(App, { target: document.getElementById('app')! });

export default app;
